// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit, Trash2, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, DollarSign, Code
} from 'lucide-react';
import api from '@/lib/api';
import { CRM_BASE } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterPriceList as PriceList,
  CrmMasterPriceListItem as PriceListItem,
  CrmMasterProduct as CrmProduct,
  CrmMasterProductCategory as ProductCategory,
} from '@easyflow/shared';
import {
  Tip, recurrenceBadge, recurrenceLabels, natureLabels, natureBadge, productStatusLabels,
  productStatusBadge, isProductEligible, ITALIAN_VAT_RATES, VatSelect, InputField, SelectField,
  Badge, ModalWrapper
} from './shared';

export function TabProdotti() { return <TabListino initialTab="prodotti" />; }
export function TabListiniPrezzi() { return <TabListino initialTab="listini" />; }

export function TabListino({ initialTab = 'prodotti' }: { initialTab?: 'prodotti' | 'listini' } = {}) {
  const [products, setProducts] = useState<CrmProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState<CrmProduct | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [subTab, setSubTab] = useState<'prodotti' | 'listini'>(initialTab);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [plLoading, setPlLoading] = useState(false);
  const [editPl, setEditPl] = useState<PriceList | null>(null);
  const [isNewPl, setIsNewPl] = useState(false);
  const [savingPl, setSavingPl] = useState(false);
  const [selectedPl, setSelectedPl] = useState<PriceList | null>(null);
  const [plItems, setPlItems] = useState<PriceListItem[]>([]);
  const [plItemsLoading, setPlItemsLoading] = useState(false);
  const [addItemProduct, setAddItemProduct] = useState('');
  const [addItemPrice, setAddItemPrice] = useState('');
  const [addItemDiscount, setAddItemDiscount] = useState('0');
  const [addItemVatRate, setAddItemVatRate] = useState('22');
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  const [productUnits, setProductUnits] = useState<{ code: string; label: string }[]>([]);

  const loadProductUnits = useCallback(async () => {
    try {
      const res = await api.get(`${CRM_BASE}/product-units`);
      if (res.success && res.data) setProductUnits(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, []);

  const loadProductCategories = useCallback(async () => {
    try {
      const res = await api.get<ProductCategory[]>(`${CRM_BASE}/product-categories`);
      if (res.success && res.data) setProductCategories(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<CrmProduct[] | { products?: CrmProduct[] }>(`${CRM_BASE}/products`);
      if (res.success && res.data) setProducts(Array.isArray(res.data) ? res.data : (res.data).products || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPriceLists = useCallback(async () => {
    setPlLoading(true);
    try {
      const res = await api.get<PriceList[]>(`${CRM_BASE}/price-lists`);
      if (res.success && res.data) setPriceLists(Array.isArray(res.data) ? res.data : []);
    } finally {
      setPlLoading(false);
    }
  }, []);

  const loadPlItems = useCallback(async (plId: string) => {
    setPlItemsLoading(true);
    try {
      const res = await api.get(`${CRM_BASE}/price-lists/${plId}/items`);
      if (res.success && res.data) setPlItems(Array.isArray(res.data) ? res.data : []);
    } finally {
      setPlItemsLoading(false);
    }
  }, []);

  useEffect(() => { void loadProducts(); void loadProductCategories(); void loadProductUnits(); }, [loadProducts, loadProductCategories, loadProductUnits]);
  useEffect(() => { if (subTab === 'listini') void loadPriceLists(); }, [subTab, loadPriceLists]);
  useEffect(() => { if (selectedPl) void loadPlItems(selectedPl.id); }, [selectedPl, loadPlItems]);

  const filtered = products.filter(p => {
    if (categoryFilter && p.category !== categoryFilter) return false;
    if (typeFilter && p.recurrence_type !== typeFilter) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => { setPage(1); }, [categoryFilter, typeFilter, pageSize]);

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))];

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post(`${CRM_BASE}/products`, editProduct);
      } else {
        await api.put(`${CRM_BASE}/products/${editProduct?.id}`, editProduct);
      }
      setEditProduct(null);
      void loadProducts();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Archiviare questo prodotto? Non comparirà più nei listini.')) return;
    await api.delete(`${CRM_BASE}/products/${id}`);
    void loadProducts();
  };

  const handleSavePl = async () => {
    setSavingPl(true);
    try {
      if (isNewPl) {
        await api.post(`${CRM_BASE}/price-lists`, editPl);
      } else {
        await api.put(`${CRM_BASE}/price-lists/${editPl?.id}`, editPl);
      }
      setEditPl(null);
      void loadPriceLists();
    } finally {
      setSavingPl(false);
    }
  };

  const handleDeletePl = async (id: string) => {
    if (!confirm('Eliminare questo listino?')) return;
    await api.delete(`${CRM_BASE}/price-lists/${id}`);
    if (selectedPl?.id === id) { setSelectedPl(null); setPlItems([]); }
    void loadPriceLists();
  };

  const handleAddPlItem = async () => {
    if (!selectedPl || !addItemProduct || !addItemPrice) return;
    await api.post(`${CRM_BASE}/price-lists/${selectedPl.id}/items`, {
      product_id: addItemProduct,
      price: parseFloat(addItemPrice),
      discount_percent: parseFloat(addItemDiscount) || 0,
      vat_rate: addItemVatRate || '22',
    });
    setAddItemProduct('');
    setAddItemPrice('');
    setAddItemDiscount('0');
    setAddItemVatRate('22');
    void loadPlItems(selectedPl.id);
    void loadPriceLists();
  };

  const handleDeletePlItem = async (itemId: string) => {
    await api.delete(`${CRM_BASE}/price-list-items/${itemId}`);
    if (selectedPl) void loadPlItems(selectedPl.id);
    void loadPriceLists();
  };

  const updateProd = (field: string) => (value: string) => setEditProduct((p) => ({ ...(p as CrmProduct), [field]: value }));
  const updatePl = (field: string) => (value: string | number | boolean) => setEditPl((p) => ({ ...(p as PriceList), [field]: value }));

  const assignedProductIds = new Set(plItems.map(i => i.product_id));
  const availableProducts = products.filter(p => isProductEligible(p) && !assignedProductIds.has(p.id));

  return (
    <div className="space-y-4">

      {subTab === 'prodotti' && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
                <option value="">Tutte le categorie</option>
                {categories.map(c => <option key={c} value={c ?? ''}>{c}</option>)}
              </select>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
                <option value="">Tutti i tipi</option>
                {Object.entries(recurrenceLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <button onClick={() => { setEditProduct({ code: '', name: '', net_price: '', vat_rate: '22', recurrence_type: 'monthly', category: '', status: 'active', nature: 'prodotto', unit_of_measure: 'pz', valid_from: '', valid_to: '', description: '' } as CrmProduct); setIsNew(true); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-foreground dark:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              <Plus className="h-4 w-4" /> Nuovo Prodotto
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Codice</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Nome</th>
                  <th className="text-right py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Prezzo Netto</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Aliquota IVA</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Ricorrenza</th>
                  <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Categoria</th>
                  <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Natura</th>
                  <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Stato</th>
                  <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">FiC</th>
                  <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Caricamento...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={10} className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Nessun prodotto trovato</td></tr>
                ) : paged.map((p) => (
                  <tr key={p.id} className="border-b border-border dark:border-gray-700/50 hover:bg-muted dark:hover:bg-gray-700/50 transition-colors">
                    <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60 font-mono text-sm">{p.code}</td>
                    <td className="py-3 px-4 text-foreground dark:text-white">{p.name}</td>
                    <td className="py-3 px-4 text-right text-muted-foreground dark:text-muted-foreground/60">{'€'} {Number(p.net_price).toFixed(2)}</td>
                    <td className="py-3 px-4 text-left text-muted-foreground dark:text-muted-foreground/60">{ITALIAN_VAT_RATES.find(v => v.code === String(p.vat_code || p.vat_rate) || v.rate === Number(p.vat_rate))?.label || `${p.vat_rate}%`}</td>
                    <td className="py-3 px-4">
                      <Badge className={recurrenceBadge[p.recurrence_type] || ''}>
                        {recurrenceLabels[p.recurrence_type] || p.recurrence_type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60">{p.category || '-'}</td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={natureBadge[p.nature ?? ''] || ''}>{natureLabels[p.nature ?? ''] || p.nature || '-'}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={productStatusBadge[p.status] || ''}>{productStatusLabels[p.status] || p.status || '-'}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {p.fic_product_id ? (
                        <Badge className="bg-green-500/20 text-green-400">Sync</Badge>
                      ) : (
                        <Badge className="bg-muted/500/20 text-muted-foreground/70">No</Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => { setEditProduct({ ...p }); setIsNew(false); }} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white"><Edit className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(p.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!loading && filtered.length > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground dark:text-muted-foreground/70">
                <span>
                  {(currentPage - 1) * pageSize + 1}
                  {'–'}
                  {Math.min(currentPage * pageSize, filtered.length)} di {filtered.length}
                </span>
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                  className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1 text-sm">
                  {[25, 50, 100].map(n => <option key={n} value={n}>{n} / pagina</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={currentPage === 1}
                  className="px-2 py-1 rounded-lg text-sm border border-border dark:border-gray-600 text-foreground dark:text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted dark:hover:bg-gray-700 transition-colors">
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-2 py-1 rounded-lg text-sm border border-border dark:border-gray-600 text-foreground dark:text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted dark:hover:bg-gray-700 transition-colors">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-3 py-1 text-sm text-muted-foreground dark:text-muted-foreground/70">
                  Pagina {currentPage} di {totalPages}
                </span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-2 py-1 rounded-lg text-sm border border-border dark:border-gray-600 text-foreground dark:text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted dark:hover:bg-gray-700 transition-colors">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}
                  className="px-2 py-1 rounded-lg text-sm border border-border dark:border-gray-600 text-foreground dark:text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted dark:hover:bg-gray-700 transition-colors">
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {subTab === 'listini' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-foreground dark:text-white font-semibold text-sm">Listini</h3>
              <button onClick={() => { setEditPl({ name: '', description: '', is_default: false, valid_from: '', valid_to: '', is_active: true } as PriceList); setIsNewPl(true); }}
                className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm">
                <Plus className="h-3 w-3" /> Nuovo
              </button>
            </div>
            {plLoading ? (
              <div className="text-muted-foreground dark:text-muted-foreground/70 text-sm py-4 text-center">Caricamento...</div>
            ) : priceLists.length === 0 ? (
              <div className="text-muted-foreground dark:text-muted-foreground/70 text-sm py-4 text-center">Nessun listino</div>
            ) : priceLists.map(pl => (
              <div key={pl.id} onClick={() => setSelectedPl(pl)}
                className={`bg-white dark:bg-gray-800 border rounded-xl p-3 cursor-pointer transition-all ${selectedPl?.id === pl.id ? 'border-blue-500 ring-1 ring-blue-500/30' : 'border-border dark:border-gray-700 hover:border-border dark:hover:border-gray-600'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-foreground dark:text-white font-medium text-sm">{pl.name}</span>
                      {pl.is_default && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Predefinito</Badge>}
                      {!pl.is_active && <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Inattivo</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-0.5">
                      {pl.items_count || 0} prodotti
                      {pl.valid_from && <span className="ml-2">dal {new Date(pl.valid_from).toLocaleDateString('it-IT')}</span>}
                      {pl.valid_to && <span> al {new Date(pl.valid_to).toLocaleDateString('it-IT')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={(e) => { e.stopPropagation(); setEditPl({ ...pl }); setIsNewPl(false); }} className="text-muted-foreground/70 hover:text-foreground dark:hover:text-white p-1"><Edit className="h-3.5 w-3.5" /></button>
                    <button onClick={(e) => { e.stopPropagation(); void handleDeletePl(pl.id); }} className="text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2">
            {selectedPl ? (
              <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-foreground dark:text-white font-semibold">
                    {selectedPl.name}
                    {selectedPl.is_default && <span className="ml-2 text-xs text-blue-400">(predefinito)</span>}
                  </h3>
                </div>

                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Prodotto</label>
                    <select value={addItemProduct} onChange={(e) => {
                      setAddItemProduct(e.target.value);
                      const prod = products.find(p => p.id === e.target.value);
                      if (prod) {
                        setAddItemPrice(String(prod.net_price));
                        setAddItemVatRate(String(prod.vat_code || prod.vat_rate || '22'));
                      }
                    }}
                      className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
                      <option value="">Seleziona prodotto...</option>
                      {availableProducts.map(p => <option key={p.id} value={p.id}>{p.name} ({'€'}{Number(p.net_price).toFixed(2)})</option>)}
                    </select>
                  </div>
                  <div className="w-28">
                    <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Prezzo</label>
                    <input type="number" value={addItemPrice} onChange={(e) => setAddItemPrice(e.target.value)}
                      className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div className="w-20">
                    <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Sc%</label>
                    <input type="number" value={addItemDiscount} onChange={(e) => setAddItemDiscount(e.target.value)}
                      className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div className="w-48">
                    <label className="block text-xs text-muted-foreground dark:text-muted-foreground/70 mb-1">Aliquota IVA</label>
                    <VatSelect value={addItemVatRate} onChange={setAddItemVatRate} />
                  </div>
                  <button onClick={handleAddPlItem} disabled={!addItemProduct || !addItemPrice}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border dark:border-gray-700">
                      <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Codice</th>
                      <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Prodotto</th>
                      <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Prezzo Base</th>
                      <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Prezzo Listino</th>
                      <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Sc%</th>
                      <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">IVA</th>
                      <th className="text-center py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {plItemsLoading ? (
                      <tr><td colSpan={7} className="py-4 text-center text-muted-foreground dark:text-muted-foreground/70 text-sm">Caricamento...</td></tr>
                    ) : plItems.length === 0 ? (
                      <tr><td colSpan={7} className="py-4 text-center text-muted-foreground dark:text-muted-foreground/70 text-sm">Nessun prodotto nel listino</td></tr>
                    ) : plItems.map(item => {
                      const vatCode = String(item.vat_rate || item.product_vat_code || item.product_vat_rate || '22');
                      const vatLabel = ITALIAN_VAT_RATES.find(v => v.code === vatCode)?.label || `${vatCode}%`;
                      return (
                      <tr key={item.id} className="border-b border-border dark:border-gray-700/50">
                        <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 font-mono text-xs">{item.product_code}</td>
                        <td className="py-2 px-3 text-foreground dark:text-white text-sm">{item.product_name}</td>
                        <td className="py-2 px-3 text-right text-muted-foreground dark:text-muted-foreground/70 text-sm">{'€'} {Number(item.base_price).toFixed(2)}</td>
                        <td className="py-2 px-3 text-right text-foreground dark:text-white text-sm font-medium">{'€'} {Number(item.price).toFixed(2)}</td>
                        <td className="py-2 px-3 text-right text-muted-foreground dark:text-muted-foreground/60 text-sm">{Number(item.discount_percent) > 0 ? `${Number(item.discount_percent)}%` : '-'}</td>
                        <td className="py-2 px-3 text-left text-muted-foreground dark:text-muted-foreground/60 text-xs">{vatLabel}</td>
                        <td className="py-2 px-3 text-center">
                          <button onClick={() => handleDeletePlItem(item.id)} className="text-muted-foreground/70 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-8 text-center">
                <DollarSign className="h-8 w-8 text-muted-foreground/70 mx-auto mb-2" />
                <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Seleziona un listino per gestirne i prodotti e i prezzi</p>
              </div>
            )}
          </div>
        </div>
      )}

      <ModalWrapper open={!!editProduct} onClose={() => setEditProduct(null)} title={isNew ? 'Nuovo Prodotto' : 'Modifica Prodotto'}>
        {editProduct && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Codice</label>
                <div className="flex gap-1.5">
                  <input type="text" value={editProduct.code || ''} onChange={(e) => updateProd('code')(e.target.value)}
                    className="flex-1 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
                  <Tip label="Genera codice automatico"><button type="button"
                    onClick={async () => {
                      try {
                        const res = await api.get(`${CRM_BASE}/products/next-code`);
                        if (res.success && res.data) updateProd('code')(res.data as string);
                      } catch {}
                    }}
                    className="px-2.5 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 rounded-lg border border-blue-500/20 transition-colors flex-shrink-0"
                  >
                    <Code className="h-4 w-4" />
                  </button></Tip>
                </div>
              </div>
              <InputField label="Nome" value={editProduct.name} onChange={updateProd('name')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Descrizione</label>
              <textarea value={editProduct.description || ''} onChange={(e) => setEditProduct((p) => ({ ...(p as CrmProduct), description: e.target.value }))}
                className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm h-20 resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Prezzo Netto (€)" value={editProduct.net_price} onChange={updateProd('net_price')} type="number" />
              <div>
                <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Aliquota IVA</label>
                <VatSelect value={String(editProduct.vat_code || editProduct.vat_rate || '22')} onChange={(v) => { updateProd('vat_code')(v); updateProd('vat_rate')(v); }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="Tipo Ricorrenza" value={editProduct.recurrence_type} onChange={updateProd('recurrence_type')}
                options={Object.entries(recurrenceLabels).map(([k, v]) => ({ value: k, label: v }))} />
              <div>
                <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Categoria</label>
                <select value={editProduct.category || ''} onChange={(e) => updateProd('category')(e.target.value)}
                  className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
                  <option value="">-- Seleziona --</option>
                  {productCategories.filter((c) => c.is_active !== false).map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="Unità di Misura" value={editProduct.unit_of_measure || 'pz'} onChange={updateProd('unit_of_measure')}
                options={productUnits.map((u) => ({ value: u.code, label: u.label }))} />
              <SelectField label="Natura" value={editProduct.nature || 'prodotto'} onChange={updateProd('nature')}
                options={Object.entries(natureLabels).map(([k, v]) => ({ value: k, label: v }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="Stato" value={editProduct.status || 'active'} onChange={updateProd('status')}
                options={Object.entries(productStatusLabels).map(([k, v]) => ({ value: k, label: v }))} />
              <div />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Valido Dal" value={editProduct.valid_from ? String(editProduct.valid_from).split('T')[0] : ''} onChange={updateProd('valid_from')} type="date" />
              <InputField label="Valido Al" value={editProduct.valid_to ? String(editProduct.valid_to).split('T')[0] : ''} onChange={updateProd('valid_to')} type="date" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditProduct(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
              <button onClick={handleSave} disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-foreground dark:text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        )}
      </ModalWrapper>

      <ModalWrapper open={!!editPl} onClose={() => setEditPl(null)} title={isNewPl ? 'Nuovo Listino' : 'Modifica Listino'}>
        {editPl && (
          <div className="space-y-4">
            <InputField label="Nome" value={editPl.name} onChange={updatePl('name')} />
            <div>
              <label className="block text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 mb-1">Descrizione</label>
              <textarea value={editPl.description || ''} onChange={(e) => setEditPl((p) => ({ ...(p as PriceList), description: e.target.value }))}
                className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm h-16 resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Valido Dal" value={editPl.valid_from ? String(editPl.valid_from).split('T')[0] : ''} onChange={updatePl('valid_from')} type="date" />
              <InputField label="Valido Al" value={editPl.valid_to ? String(editPl.valid_to).split('T')[0] : ''} onChange={updatePl('valid_to')} type="date" />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-foreground dark:text-muted-foreground/60 cursor-pointer">
                <input type="checkbox" checked={editPl.is_default || false} onChange={(e) => updatePl('is_default')(e.target.checked)}
                  className="rounded border-border dark:border-gray-600" />
                Predefinito
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground dark:text-muted-foreground/60 cursor-pointer">
                <input type="checkbox" checked={editPl.is_active !== false} onChange={(e) => updatePl('is_active')(e.target.checked)}
                  className="rounded border-border dark:border-gray-600" />
                Attivo
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditPl(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
              <button onClick={handleSavePl} disabled={savingPl || !editPl.name}
                className="bg-blue-600 hover:bg-blue-700 text-foreground dark:text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
                {savingPl ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        )}
      </ModalWrapper>
    </div>
  );
}

// ==================== Filtri avanzati condivisi (Task #3090) ====================
